/**
 * Word document export using docx-templates.
 * Generates QQI-compatible programme descriptor documents from a tagged template.
 *
 * This is the single, canonical Programme Descriptor Word export: the data
 * built by {@link buildDescriptorData} embeds the proposed programme schedule
 * as a section (`scheduleSections`) rather than emitting it as a separate
 * standalone artifact (PD-074/PD-075).
 *
 * The template asset (under `public/assets`) is resolved by descriptor variant
 * from {@link resolveDescriptorTemplate}, and contains `{field}` and
 * `{FOR}...{END-FOR}` commands processed by docx-templates. The data is built
 * by {@link buildDescriptorData} in `descriptor-data.ts`.
 *
 * @module export/word
 */

import { saveAs } from "file-saver";

import { getAwardStandards } from "@/state/store";
import type { Programme } from "@/types";
import type { ProviderContact } from "@/types/provider";
import { loadCasData } from "@/utils/cas-data";

import { buildDescriptorData } from "./descriptor-data";
import { DESCRIPTOR_TEMPLATES } from "./descriptor-template-registry";
import type { DescriptorVariant } from "./descriptor-variant";
import { resolveDescriptorVariant } from "./descriptor-variant";

/** Result of a descriptor Word export, exposing the template used. */
export interface DescriptorExportResult {
  /** Machine filename of the template asset used for the export. */
  fileName: string;
  /** Human-readable name of the template used. */
  friendlyName: string;
  /** QQI version/designation of the template asset used for the export. */
  templateVersion: string;
  /** Remaining export blockers (empty when the programme is complete). */
  exportBlockers: string[];
}

/**
 * Loads the descriptor template buffer for the given asset filename.
 *
 * @throws {Error} If the resolved template cannot be loaded.
 */
async function loadDescriptorTemplate(fileName: string): Promise<ArrayBuffer> {
  // Absolute, not relative: see the matching note in state/store.ts — this
  // is called from deep client routes now, not just "/".
  const baseUrl = import.meta.env.BASE_URL ?? "/";
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const tplRes = await fetch(`${normalizedBaseUrl}assets/${fileName}`);
  if (!tplRes.ok) {
    throw new Error("Failed to load Word template");
  }
  return tplRes.arrayBuffer();
}

async function loadCreateReport(): Promise<typeof import("docx-templates/lib/browser")> {
  return import("docx-templates/lib/browser");
}

/**
 * Exports programme descriptor as a Word document.
 * Loads the tagged template, builds data from programme state,
 * and generates the output document.
 *
 * The export is produced as a partial preview even when the programme data is
 * incomplete: the document is always generated, and the remaining required-data
 * blockers are returned so the caller can list what still needs completing
 * (PD-019).
 *
 * The template variant is auto-resolved from the programme via
 * {@link resolveDescriptorVariant}, but a caller may pass `variantOverride` to
 * force a specific variant's template while the default stays the resolved one.
 *
 * @param p The programme to export.
 * @param variantOverride Optional variant to force; defaults to the variant
 *   resolved from `p` by {@link resolveDescriptorVariant}.
 * @param versionId Optional programme version to project into the descriptor.
 * @param programmeLeadContact The programme lead, for the Programme Manager block.
 * @returns The used template's machine `fileName`, human `friendlyName`,
 *   `templateVersion`, and the list of remaining export blockers (empty when
 *   the programme is complete).
 * @throws {Error} If template loading fails
 */
export async function exportProgrammeDescriptorWord(
  p: Programme,
  variantOverride?: DescriptorVariant,
  versionId?: string,
  programmeLeadContact?: ProviderContact,
): Promise<DescriptorExportResult> {
  const variant = variantOverride ?? resolveDescriptorVariant(p);
  const template = DESCRIPTOR_TEMPLATES[variant];
  const awardStandards = await getAwardStandards().catch(() => []);
  // CAS exports resolve authoritative award titles from the CAS dataset; a
  // load failure degrades to the programme's own inline titles rather than
  // blocking the export.
  const casAwards = variant === "cas" ? await loadCasData().catch(() => undefined) : undefined;
  const tplBuf = await loadDescriptorTemplate(template.fileName);
  const { createReport } = await loadCreateReport();

  const data = buildDescriptorData(p, {
    awardStandards,
    ...(casAwards ? { casAwards } : {}),
    ...(versionId ? { versionId } : {}),
    ...(programmeLeadContact ? { programmeLeadContact } : {}),
  });

  const output = await createReport({
    template: new Uint8Array(tplBuf),
    data,
    cmdDelimiter: ["{", "}"],
    processLineBreaks: true,
    failFast: false,
    noSandbox: true,
  });

  const blob = new Blob([output as BlobPart], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });

  const safeTitle = (p.title || "programme")
    .replace(/[^a-z0-9\-\s]/gi, "")
    .trim()
    .replace(/\s+/g, "_");
  const version = versionId
    ? (p.versions ?? []).find((candidate) => candidate.id === versionId)
    : undefined;
  const safeVersion = (version?.label ?? version?.code ?? "")
    .replace(/[^a-z0-9\-\s]/gi, "")
    .trim()
    .replace(/\s+/g, "_");
  const versionSuffix = safeVersion ? `_${safeVersion}` : "";
  saveAs(blob, `${safeTitle || "programme"}${versionSuffix}_programme_descriptor.docx`);

  return {
    fileName: template.fileName,
    friendlyName: template.friendlyName,
    templateVersion: template.templateVersion,
    exportBlockers: data.exportBlockers ?? [],
  };
}
